/**
 * React hooks for applying SEO metadata to the DOM.
 *
 * Thin wrappers — these hooks set document.title, meta tags, canonical links,
 * and JSON-LD structured data. The actual data can come from payloads.ts
 * (for prerenderable pages) or be passed directly (for auth-gated pages).
 */

import { useEffect } from 'react';

// ---------------------------------------------------------------------------
// useSEO
// ---------------------------------------------------------------------------

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
}

/**
 * Set document title, meta description, canonical URL, OG tags, and robots.
 * Updates existing tags or creates new ones. No cleanup on unmount — each
 * page overrides the previous values.
 */
export function useSEO({
  title = 'Bar Exam & Law School Prep | JD Simplified',
  description = 'AI-powered platform for law students and bar takers. Connected notes, outlines, case briefs, flashcards, and study tools to help you learn faster and master the law.',
  image = '/images/og-jds-default.png',
  url = 'https://jdsimplified.com',
  type = 'website',
  noindex = false,
}: SEOProps = {}) {
  useEffect(() => {
    document.title = title;

    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image.startsWith('http') ? image : `${url}${image}`, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'JD Simplified', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image.startsWith('http') ? image : `${url}${image}`);

    // Additional SEO tags
    updateMetaTag('author', 'JD Simplified, LLC');
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    return () => {
      // No cleanup — next page overrides
    };
  }, [title, description, image, url, type, noindex]);
}

// ---------------------------------------------------------------------------
// useStructuredData
// ---------------------------------------------------------------------------

/**
 * Inject page-specific JSON-LD structured data into <head>.
 * Manages a single script tag per id — updates on re-render, removes on unmount.
 */
export function useStructuredData(id: string, schema: Record<string, unknown> | null) {
  useEffect(() => {
    if (!schema) return;

    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      script?.remove();
    };
  }, [id, schema]);
}

// ---------------------------------------------------------------------------
// Structured data helpers (used by HomePage and other pages)
// ---------------------------------------------------------------------------

export { getOrganizationSchema, getWebsiteSchema, getFAQSchema } from './schema-helpers';
